import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Calendar, Clock, MapPin, Users, X, Trash2, Edit2, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const FixturesDiary = () => {
  const [fixtures, setFixtures] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [suggestedPlayers, setSuggestedPlayers] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);
  const [expandedFixture, setExpandedFixture] = useState(null);
  const [competitionFilter, setCompetitionFilter] = useState('all');
  const [form, setForm] = useState({
    date: '', time: '', home_team: '', away_team: '', ground: '', competition: '', linked_players: [], notes: ''
  });

  const fetchFixtures = useCallback(async () => {
    try {
      const [fixturesRes, playersRes] = await Promise.all([
        api.get('/fixtures-diary'),
        api.get('/players')
      ]);
      setFixtures(fixturesRes.data || []);
      setAllPlayers(playersRes.data || []);
    } catch (error) {
      toast.error('Failed to load fixtures');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFixtures(); }, [fetchFixtures]);

  // Auto-suggest players when teams change
  useEffect(() => {
    const suggest = async () => {
      if (!form.home_team && !form.away_team) { setSuggestedPlayers([]); return; }
      try {
        const params = new URLSearchParams();
        if (form.home_team) params.append('home_team', form.home_team);
        if (form.away_team) params.append('away_team', form.away_team);
        const res = await api.get(`/fixtures-diary/suggest-players?${params}`);
        setSuggestedPlayers(res.data || []);
      } catch (error) {
        setSuggestedPlayers([]);
      }
    };
    const timer = setTimeout(suggest, 500);
    return () => clearTimeout(timer);
  }, [form.home_team, form.away_team]);

  const resetForm = () => {
    setForm({ date: '', time: '', home_team: '', away_team: '', ground: '', competition: '', linked_players: [], notes: '' });
    setEditingId(null);
    setShowForm(false);
    setSuggestedPlayers([]);
    setPlayerSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.home_team || !form.away_team) {
      toast.error('Date, home team and away team are required');
      return;
    }
    try {
      if (editingId) {
        await api.put(`/fixtures-diary/${editingId}`, form);
        toast.success('Fixture updated');
      } else {
        await api.post('/fixtures-diary', form);
        toast.success('Fixture added');
      }
      resetForm();
      fetchFixtures();
    } catch (error) {
      toast.error('Failed to save fixture');
    }
  };

  const deleteFixture = async (id) => {
    if (!window.confirm('Delete this fixture?')) return;
    try {
      await api.delete(`/fixtures-diary/${id}`);
      toast.success('Fixture deleted');
      fetchFixtures();
    } catch (error) {
      toast.error('Failed to delete fixture');
    }
  };

  const editFixture = (fixture) => {
    setForm({
      date: fixture.date || '',
      time: fixture.time || '',
      home_team: fixture.home_team || '',
      away_team: fixture.away_team || '',
      ground: fixture.ground || '',
      competition: fixture.competition || '',
      linked_players: fixture.linked_players || [],
      notes: fixture.notes || ''
    });
    setEditingId(fixture.id);
    setShowForm(true);
  };

  const addPlayer = (player) => {
    if (form.linked_players.some(p => p.player_id === player.id)) return;
    setForm(prev => ({
      ...prev,
      linked_players: [...prev.linked_players, { player_id: player.id, player_name: player.name }]
    }));
    setPlayerSearch('');
    setShowPlayerDropdown(false);
  };

  const removePlayer = (playerId) => {
    setForm(prev => ({
      ...prev,
      linked_players: prev.linked_players.filter(p => p.player_id !== playerId)
    }));
  };

  const filteredSearchPlayers = allPlayers.filter(p =>
    p.name.toLowerCase().includes(playerSearch.toLowerCase()) &&
    !form.linked_players.some(lp => lp.player_id === p.id)
  );

  // Get unique competitions for filter
  const competitions = [...new Set(fixtures.map(f => f.competition).filter(Boolean))].sort();

  // Split into upcoming and past with competition filter
  const filtered = competitionFilter === 'all' ? fixtures : fixtures.filter(f => f.competition === competitionFilter);
  const today = new Date().toISOString().split('T')[0];
  const upcoming = filtered.filter(f => f.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const past = filtered.filter(f => f.date < today).sort((a, b) => b.date.localeCompare(a.date));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
                Fixtures Diary
              </h1>
              <p className="text-lg text-zinc-300">{upcoming.length} upcoming &bull; {past.length} completed</p>
            </div>
            <button
              onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}
              data-testid="add-fixture-btn"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3 transition-all"
            >
              <Plus className="w-5 h-5" />
              Add Fixture
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Competition Filter */}
        {competitions.length > 0 && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setCompetitionFilter('all')} data-testid="filter-comp-all"
                className={`px-3 py-1.5 text-xs font-bold uppercase rounded-sm transition-all ${
                  competitionFilter === 'all' ? 'bg-white text-black' : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                }`}>
                All Competitions
              </button>
              {competitions.map(comp => (
                <button key={comp} onClick={() => setCompetitionFilter(comp)} data-testid={`filter-comp-${comp}`}
                  className={`px-3 py-1.5 text-xs font-bold uppercase rounded-sm transition-all ${
                    competitionFilter === comp ? 'bg-white text-black' : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                  }`}>
                  {comp}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <div className="glass-card p-6 mb-8" data-testid="fixture-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold uppercase tracking-tight text-white">
                {editingId ? 'Edit Fixture' : 'New Fixture'}
              </h2>
              <button onClick={resetForm} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Date *</label>
                  <input type="date" required value={form.date} onChange={e => setForm({...form, date: e.target.value})}
                    data-testid="fixture-date-input"
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Kick-Off Time</label>
                  <input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})}
                    data-testid="fixture-time-input"
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Ground</label>
                  <input type="text" placeholder="Stadium / venue" value={form.ground} onChange={e => setForm({...form, ground: e.target.value})}
                    data-testid="fixture-ground-input"
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Home Team *</label>
                  <input type="text" required placeholder="Home team" value={form.home_team} onChange={e => setForm({...form, home_team: e.target.value})}
                    data-testid="fixture-home-input"
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Away Team *</label>
                  <input type="text" required placeholder="Away team" value={form.away_team} onChange={e => setForm({...form, away_team: e.target.value})}
                    data-testid="fixture-away-input"
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Competition</label>
                  <input type="text" placeholder="e.g. WSL, Championship" value={form.competition} onChange={e => setForm({...form, competition: e.target.value})}
                    data-testid="fixture-competition-input" list="competition-suggestions"
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none" />
                  {competitions.length > 0 && (
                    <datalist id="competition-suggestions">
                      {competitions.map(c => <option key={c} value={c} />)}
                    </datalist>
                  )}
                </div>
              </div>

              {/* Auto-suggested players */}
              {suggestedPlayers.length > 0 && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Suggested Players (based on clubs)</label>
                  <div className="flex flex-wrap gap-2">
                    {suggestedPlayers.map(p => {
                      const alreadyLinked = form.linked_players.some(lp => lp.player_id === p.id);
                      return (
                        <button key={p.id} type="button" onClick={() => !alreadyLinked && addPlayer(p)}
                          className={`px-3 py-1.5 rounded text-xs font-bold uppercase transition-all ${
                            alreadyLinked
                              ? 'bg-green-500/20 text-green-400 border border-green-500/30 cursor-default'
                              : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
                          }`}>
                          {p.name} ({p.current_club})
                          {alreadyLinked && ' ✓'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Manual player search */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Link Players</label>
                {form.linked_players.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {form.linked_players.map(p => (
                      <span key={p.player_id} className="inline-flex items-center gap-1 px-3 py-1 bg-white/10 text-white text-xs font-bold uppercase rounded border border-white/10">
                        {p.player_name}
                        <button type="button" onClick={() => removePlayer(p.player_id)} className="ml-1 hover:text-red-400">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input type="text" placeholder="Search players to add..." value={playerSearch}
                    onChange={e => { setPlayerSearch(e.target.value); setShowPlayerDropdown(true); }}
                    onFocus={() => setShowPlayerDropdown(true)}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md pl-10 pr-4 py-2.5 text-white placeholder:text-zinc-600 outline-none text-sm" />
                  {showPlayerDropdown && playerSearch && filteredSearchPlayers.length > 0 && (
                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-zinc-900 border border-white/10 rounded-md max-h-48 overflow-y-auto">
                      {filteredSearchPlayers.slice(0, 10).map(p => (
                        <button key={p.id} type="button" onClick={() => addPlayer(p)}
                          className="w-full text-left px-4 py-2 hover:bg-white/10 text-sm text-white flex items-center justify-between">
                          <span>{p.name}</span>
                          <span className="text-zinc-500 text-xs">{p.current_club || p.position}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Notes</label>
                <input type="text" placeholder="Optional notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                  className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none" />
              </div>

              <div className="flex gap-2">
                <button type="submit" data-testid="save-fixture-btn"
                  className="bg-white text-black hover:bg-zinc-200 font-bold uppercase px-6 py-2 rounded-sm transition-all">
                  {editingId ? 'Update' : 'Add Fixture'}
                </button>
                <button type="button" onClick={resetForm}
                  className="bg-white/10 text-white hover:bg-white/20 px-6 py-2 rounded-sm transition-all">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Upcoming Fixtures */}
        {upcoming.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold uppercase tracking-widest text-zinc-400 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" /> Upcoming ({upcoming.length})
            </h2>
            <div className="space-y-3">
              {upcoming.map(fixture => (
                <FixtureCard key={fixture.id} fixture={fixture} onEdit={editFixture} onDelete={deleteFixture}
                  expanded={expandedFixture === fixture.id} onToggle={() => setExpandedFixture(expandedFixture === fixture.id ? null : fixture.id)} />
              ))}
            </div>
          </div>
        )}

        {/* Past Fixtures */}
        {past.length > 0 && (
          <div>
            <h2 className="text-lg font-bold uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" /> Past ({past.length})
            </h2>
            <div className="space-y-3">
              {past.map(fixture => (
                <FixtureCard key={fixture.id} fixture={fixture} onEdit={editFixture} onDelete={deleteFixture} isPast
                  expanded={expandedFixture === fixture.id} onToggle={() => setExpandedFixture(expandedFixture === fixture.id ? null : fixture.id)} />
              ))}
            </div>
          </div>
        )}

        {fixtures.length === 0 && (
          <div className="glass-card p-12 text-center">
            <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">No Fixtures Yet</h2>
            <p className="text-zinc-400">Add your first fixture to start tracking matches and linking your players.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const FixtureCard = ({ fixture, onEdit, onDelete, isPast, expanded, onToggle }) => {
  const playerCount = (fixture.linked_players || []).length;
  return (
    <div className={`glass-card overflow-hidden transition-all ${isPast ? 'opacity-70' : ''}`} data-testid={`fixture-${fixture.id}`}>
      <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/5 transition-colors" onClick={onToggle}>
        {/* Date block */}
        <div className="flex-shrink-0 w-16 text-center">
          <p className="text-2xl font-bold text-white tabular-nums">{fixture.date ? new Date(fixture.date).getDate() : ''}</p>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">
            {fixture.date ? new Date(fixture.date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : ''}
          </p>
        </div>

        <div className="h-10 w-px bg-white/10 flex-shrink-0"></div>

        {/* Teams */}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-white text-sm uppercase tracking-wide">
            {fixture.home_team} <span className="text-zinc-500 font-normal mx-1">vs</span> {fixture.away_team}
          </p>
          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-400">
            {fixture.time && (
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fixture.time}</span>
            )}
            {fixture.ground && (
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {fixture.ground}</span>
            )}
            {fixture.competition && (
              <span className="px-1.5 py-0.5 bg-white/10 rounded text-[10px] font-bold uppercase">{fixture.competition}</span>
            )}
          </div>
        </div>

        {/* Player count badge */}
        {playerCount > 0 && (
          <div className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 bg-white/10 rounded text-xs font-bold text-white uppercase">
            <Users className="w-3.5 h-3.5" /> {playerCount} player{playerCount > 1 ? 's' : ''}
          </div>
        )}

        {/* Actions */}
        <div className="flex-shrink-0 flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onEdit(fixture); }} className="p-2 hover:bg-white/10 rounded text-zinc-400 hover:text-white transition-colors">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(fixture.id); }} className="p-2 hover:bg-red-500/20 rounded text-zinc-400 hover:text-red-400 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-white/5 p-4 bg-black/20">
          {(fixture.linked_players || []).length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Linked Players</p>
              <div className="flex flex-wrap gap-2">
                {fixture.linked_players.map(p => (
                  <span key={p.player_id} className="px-3 py-1 bg-white/10 text-white text-xs font-bold uppercase rounded border border-white/10">
                    {p.player_name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {fixture.notes && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Notes</p>
              <p className="text-sm text-zinc-300">{fixture.notes}</p>
            </div>
          )}
          {!(fixture.linked_players || []).length && !fixture.notes && (
            <p className="text-sm text-zinc-500">No additional details.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default FixturesDiary;
